import React, { useEffect, useState } from "react";
import { Heart } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";

/**
 * Toggleable save (wishlist) button.
 *   <SaveButton kind="product" id={product_id} />
 *   <SaveButton kind="video" id={video_id} variant="overlay"/>
 *   <SaveButton kind="stream" id={stream_id} />
 */
export default function SaveButton({ kind, id, variant = "default", className = "", onChange }) {
  const { user } = useAuth();
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !id) { setSaved(false); return; }
    api.get("/me/saved").then(({ data }) => {
      const list = data[`${kind}s`] || [];
      setSaved(list.includes(id));
    }).catch(() => {});
  }, [user, kind, id]);

  const toggle = async (e) => {
    e?.preventDefault?.(); e?.stopPropagation?.();
    if (!user) {
      window.location.href = "/login";
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/me/save/${kind}/${id}`);
      setSaved(!!data.saved);
      onChange?.(!!data.saved);
    } catch {} finally { setBusy(false); }
  };

  if (variant === "overlay") {
    return (
      <button onClick={toggle} disabled={busy} data-testid={`save-${kind}-${id}`}
        aria-label={saved ? "Unsave" : "Save"}
        className={`w-11 h-11 rounded-full flex items-center justify-center transition active:scale-90 ${saved ? "bg-[#FF007F] text-white" : "bg-black/40 backdrop-blur text-white hover:bg-black/60"} ${className}`}>
        <Heart size={20} fill={saved ? "currentColor" : "none"} strokeWidth={2.5}/>
      </button>
    );
  }
  if (variant === "icon") {
    return (
      <button onClick={toggle} disabled={busy} data-testid={`save-${kind}-${id}`}
        aria-label={saved ? "Unsave" : "Save"}
        className={`w-9 h-9 rounded-full bg-[#261534] border border-[#3D2845] hover:border-[#FF007F] flex items-center justify-center transition ${saved ? "text-[#FF007F]" : "text-[#A1A1AA]"} ${className}`}>
        <Heart size={16} fill={saved ? "currentColor" : "none"} strokeWidth={2.5}/>
      </button>
    );
  }
  return (
    <button onClick={toggle} disabled={busy} data-testid={`save-${kind}-${id}`}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-full font-bold uppercase tracking-wider text-xs transition border-2 ${saved ? "bg-[#FF007F]/15 border-[#FF007F] text-[#FF007F]" : "bg-[#261534] border-[#3D2845] hover:border-[#FF007F]"} ${className}`}>
      <Heart size={14} fill={saved ? "currentColor" : "none"} strokeWidth={2.5}/>
      {saved ? "Saved" : "Save"}
    </button>
  );
}
